import {
  Avatar,
  Box,
  Center,
  Flex,
  HStack,
  IconButton,
  LinkBox,
  LinkOverlay,
  MenuTrigger,
  Spacer,
  useDisclosure,
  WrapItem,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FaQuestion } from "react-icons/fa";
import { FiMenu } from "react-icons/fi";
import { Else, If, Then } from "react-if";
import { Link as ReactRouterLink } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { MARKET_DATA_SELECTION_TUTORIAL_URL } from "../../../config/constants";
import { Login, Logout, authSelector } from "../../../lib/authentication/authenticationSlice";
import { useAuthContext } from "../../../lib/authentication/components/useAuthContext";
import { Logo } from "../../../logo";
import { ChackraUIBaseModal } from "../../chackraModal/chackraBaseModal";
import { HelpModalBody } from "../../helpModalBody/helpModalBody";
import { ColorModeButton } from "../../ui/color-mode";
import { MenuContent, MenuItem, MenuItemGroup, MenuRoot, MenuSeparator } from "../../ui/menu";
import { MenuUser } from "../../userMenu";
import { useLayoutContext } from "../useLayoutContext";

import { GlobalLoadingBar } from "./GlobalLoadingBar";
import NavButtons from "./NavButtons";
import AppMenuItem from "./navItem/navItem";

export const UserMenu = () => {
  const dispatch = useAppDispatch();
  const { isLogged } = useAuthContext();

  const authStore = useAppSelector(authSelector);
  const user = authStore.data;

  const { t } = useTranslation("template");

  return (
    <MenuRoot>
      <MenuTrigger>
        <If condition={isLogged}>
          <Then>
            <Avatar.Root>
              <Avatar.Fallback name={user?.userInfo?.username ?? t("menu.user")} />
            </Avatar.Root>
          </Then>
          <Else>
            <Avatar.Root>
              <Avatar.Icon />
            </Avatar.Root>
          </Else>
        </If>
      </MenuTrigger>
      <MenuContent>
        <MenuItemGroup title={t("menu.options")}>
          <MenuItem value={"mode-switch"}>
            Theme <ColorModeButton />
          </MenuItem>
        </MenuItemGroup>
        <MenuSeparator />
        <MenuItemGroup title={t("menu.account")}>
          <If condition={isLogged}>
            <Then>
              <WrapItem>
                <MenuItem value={user?.userInfo?.username ?? t("menu.user")}>
                  {user?.userInfo?.username ?? t("menu.user")}
                </MenuItem>
              </WrapItem>
              <MenuItem value={"exit"} onClick={async () => dispatch(Logout())}>
                {t("menu.exit")}
              </MenuItem>
            </Then>
            <Else>
              <WrapItem>
                <MenuItem value={"login"} onClick={async () => dispatch(Login())}>
                  {t("menu.login")}
                </MenuItem>
              </WrapItem>
            </Else>
          </If>
        </MenuItemGroup>
      </MenuContent>
    </MenuRoot>
  );
};

const Header = () => {
  const { isMobileSiderOpen, setMobileSiderOpen } = useLayoutContext();
  const { open, onClose, onOpen } = useDisclosure();
  return (
    <>
      <Box
        as="header"
        shadow={"sm"}
        bg={"header"}
        borderBottomRadius={"md"}
        borderBottomWidth="1px"
        borderBottomColor={"headerBorder"}
      >
        <Flex paddingTop={"5px"} paddingBottom={"5px"}>
          <HStack gap={2} pl={2}>
            <LinkBox>
              <Center>
                <LinkOverlay asChild>
                  <ReactRouterLink to="/">
                    <Logo />
                  </ReactRouterLink>
                </LinkOverlay>
              </Center>
            </LinkBox>
          </HStack>
          <Spacer />
          <NavButtons />
          <HStack gap={4}>
            <Box key={"help"}>
              <AppMenuItem action={onOpen} icon={FaQuestion} label={""} />
            </Box>
          </HStack>

          <HStack gap={2} pr={2} ml={2}>
            <Center>
              <MenuUser />
            </Center>
            <Center display={{ base: "block", lg: "none" }}>
              <If condition={!isMobileSiderOpen}>
                <Then>
                  <IconButton
                    variant="outline"
                    aria-label="open menu"
                    onClick={() => {
                      setMobileSiderOpen(true);
                    }}
                  >
                    <FiMenu />
                  </IconButton>
                </Then>
              </If>
            </Center>
          </HStack>
        </Flex>
        <GlobalLoadingBar />
      </Box>
      <ChackraUIBaseModal
        open={open}
        onClose={onClose}
        title="Help"
        body={<HelpModalBody url={MARKET_DATA_SELECTION_TUTORIAL_URL} />}
        size={"xl"}
        footerCloseButton={true}
      />
    </>
  );
};

export default Header;
